import ApiService from '../api'

class ProviderMailService {
  /**
   * Check email provider health/configuration status
   * @returns {Promise}
   */
  async getProviderHealth() {
    try {
      return await ApiService.get('/email-management/provider/health')
    } catch (error) {
      console.error('Get provider health error:', error)
      throw error
    }
  }

  /**
   * Get provider email summary statistics
   * @param {Object} params - Query parameters {from, to}
   * @returns {Promise}
   */
  async getProviderSummary(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      if (params.from) queryParams.append('from', params.from)
      if (params.to) queryParams.append('to', params.to)

      return await ApiService.get(`/email-management/provider/summary?${queryParams.toString()}`)
    } catch (error) {
      console.error('Get provider summary error:', error)
      throw error
    }
  }

  /**
   * Get provider email events with filtering
   * @param {Object} params - Query parameters {category, search, from, to, page, per_page}
   * @returns {Promise}
   */
  async getProviderEvents(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      if (params.category) queryParams.append('category', params.category)
      if (params.search) queryParams.append('search', params.search)
      if (params.from) queryParams.append('from', params.from)
      if (params.to) queryParams.append('to', params.to)
      if (params.page) queryParams.append('page', params.page)
      if (params.per_page) queryParams.append('per_page', params.per_page)

      return await ApiService.get(`/email-management/provider/events?${queryParams.toString()}`)
    } catch (error) {
      console.error('Get provider events error:', error)
      throw error
    }
  }

  /**
   * Refresh the stored delivery history from the provider now, instead of
   * waiting for the next scheduled sync
   * @returns {Promise}
   */
  async syncNow() {
    try {
      return await ApiService.post('/email-management/provider/sync', {}, {})
    } catch (error) {
      console.error('Sync provider events error:', error)
      throw error
    }
  }

  /**
   * Format date for API queries (YYYY-MM-DD)
   * @param {Date|string} date - Date to format
   * @returns {string}
   */
  formatDateForApi(date) {
    if (!date) return ''
    if (typeof date === 'string') return date
    const d = new Date(date)
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${d.getFullYear()}-${month}-${day}`
  }

  /**
   * Get date range for last N days
   * @param {number} days - Number of days back
   * @returns {Object} {from, to}
   */
  getDateRangeLastDays(days = 30) {
    const to = new Date()
    const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000)

    return {
      from: this.formatDateForApi(from),
      to: this.formatDateForApi(to)
    }
  }

  /**
   * Get status badge class for Bootstrap styling
   * @param {string} status - Email status
   * @returns {string}
   */
  getStatusBadgeClass(status) {
    const statusMap = {
      delivered: 'bg-success',
      bounced: 'bg-danger',
      not_delivered: 'bg-danger',
      invalid: 'bg-warning text-dark',
      unsubscribed: 'bg-secondary',
      complaint: 'bg-danger',
      sent: 'bg-info text-dark'
    }
    return statusMap[status?.toLowerCase()] || 'bg-secondary'
  }

  /**
   * Get status icon class (Bootstrap Icons)
   * @param {string} status - Email status
   * @returns {string}
   */
  getStatusIcon(status) {
    const iconMap = {
      delivered: 'bi-check-circle-fill',
      bounced: 'bi-x-circle-fill',
      not_delivered: 'bi-exclamation-circle-fill',
      invalid: 'bi-exclamation-triangle-fill',
      unsubscribed: 'bi-dash-circle-fill',
      complaint: 'bi-exclamation-circle-fill',
      sent: 'bi-envelope-open'
    }
    return iconMap[status?.toLowerCase()] || 'bi-question-circle'
  }

  /**
   * Badges for what the provider reported about one email: its delivery
   * status, plus Opened and Clicked when they happened (a click counts as an open)
   * @param {Object|null} delivery - The `delivery` summary of an email history row
   * @returns {Array<{label: string, class: string, icon: string}>}
   */
  getDeliveryBadges(delivery) {
    if (!delivery) return []

    const badges = []
    const status = delivery.status?.toLowerCase()
    if (status && status !== 'sent') {
      const label = status.replace(/_/g, ' ')
      badges.push({
        label: label.charAt(0).toUpperCase() + label.slice(1),
        class: this.getStatusBadgeClass(status),
        icon: this.getStatusIcon(status)
      })
    }
    if (delivery.opened) {
      badges.push({ label: 'Opened', class: 'bg-info text-dark', icon: this.getEventIcon('opened') })
    }
    if (delivery.clicked) {
      badges.push({ label: 'Clicked', class: 'bg-info text-dark', icon: this.getEventIcon('clicked') })
    }
    return badges
  }

  /**
   * Icon (Bootstrap Icons) for one step of a message's timeline
   * @param {string} type - Timeline step type (accepted, delivered, opened, ...)
   * @returns {string}
   */
  getEventIcon(type) {
    const iconMap = {
      accepted: 'bi-send-check',
      delivered: 'bi-check-circle-fill',
      opened: 'bi-envelope-open-fill',
      clicked: 'bi-cursor-fill',
      deferred: 'bi-hourglass-split',
      suppressed: 'bi-slash-circle-fill',
      bounced: 'bi-x-circle-fill',
      invalid: 'bi-exclamation-triangle-fill',
      unsubscribed: 'bi-dash-circle-fill',
      complaint: 'bi-exclamation-circle-fill'
    }
    return iconMap[type?.toLowerCase()] || 'bi-record-circle'
  }

  /**
   * Text colour class for one step of a message's timeline
   * @param {string} type - Timeline step type
   * @returns {string}
   */
  getEventColor(type) {
    const colorMap = {
      accepted: 'text-secondary',
      delivered: 'text-success',
      opened: 'text-info',
      clicked: 'text-info',
      deferred: 'text-warning',
      suppressed: 'text-warning',
      bounced: 'text-danger',
      invalid: 'text-warning',
      unsubscribed: 'text-secondary',
      complaint: 'text-danger'
    }
    return colorMap[type?.toLowerCase()] || 'text-secondary'
  }

  /**
   * Parse category filter for human-readable display
   * @param {string} category - Category value
   * @returns {string}
   */
  getCategoryLabel(category) {
    const categoryMap = {
      all: 'All Events',
      delivered: 'Delivered',
      problems: 'Problems',
      opened: 'Opened'
    }
    return categoryMap[category?.toLowerCase()] || category
  }

  /**
   * What the message's status means, for the top of the details dialog
   * @param {Object} message - A row from the events list
   * @returns {{variant: string, icon: string, title: string, text: string}}
   */
  describeStatus(message) {
    const status = message?.status?.toLowerCase()
    const reason = message?.reason_summary || message?.reason || 'The mail provider did not report a reason.'

    switch (status) {
      case 'delivered':
        return {
          variant: 'success',
          icon: 'bi-check-circle-fill',
          title: 'Delivered',
          text: !message.opened
            ? 'Not opened yet.'
            : message.opened_inferred
              ? 'Opened (a link was clicked).'
              : `Opened ${message.open_count} time${message.open_count === 1 ? '' : 's'}.`
        }
      case 'bounced':
        return { variant: 'danger', icon: 'bi-x-circle-fill', title: 'Bounced: the email was not delivered', text: reason }
      case 'invalid':
        return { variant: 'warning', icon: 'bi-exclamation-triangle-fill', title: 'Not delivered: invalid address', text: reason }
      case 'not_delivered':
        return {
          variant: 'danger',
          icon: 'bi-exclamation-circle-fill',
          title: message.next_try ? 'Not delivered yet: it will be retried' : 'Not delivered',
          text: reason
        }
      case 'complaint':
        return { variant: 'danger', icon: 'bi-exclamation-circle-fill', title: 'The recipient marked this email as spam', text: reason }
      case 'unsubscribed':
        return { variant: 'warning', icon: 'bi-dash-circle-fill', title: 'The recipient has unsubscribed', text: reason }
      default:
        return {
          variant: 'info',
          icon: 'bi-envelope-open',
          title: 'Accepted by the mail provider',
          text: 'No delivery result has been reported yet.'
        }
    }
  }

  /**
   * Check if status is a problem status
   * @param {string} status - Email status
   * @returns {boolean}
   */
  isProblemStatus(status) {
    const problemStatuses = ['bounced', 'not_delivered', 'invalid', 'complaint']
    return problemStatuses.includes(status?.toLowerCase())
  }
}

export default new ProviderMailService()
